class ProductTestimonials extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    this.track = this.querySelector('[data-testimonials-track]');
    this.slides = [...this.querySelectorAll('[data-testimonial-slide]')];
    this.dots = [...this.querySelectorAll('[data-testimonial-dot]')];
    this.currentIndex = 0;
    this.scrollFrame = null;
    this.autoplayTimer = null;

    if (!this.track || this.slides.length < 2) return;

    this.dots.forEach((dot) => {
      dot.addEventListener('click', () => {
        this.goTo(Number(dot.dataset.index));
        this.restartAutoplay();
      });
    });

    this.track.addEventListener('scroll', () => this.handleScroll(), { passive: true });
    this.addEventListener('pointerenter', () => this.stopAutoplay());
    this.addEventListener('pointerleave', () => this.startAutoplay());
    this.addEventListener('focusin', () => this.stopAutoplay());
    this.addEventListener('focusout', (event) => {
      if (!this.contains(event.relatedTarget)) this.startAutoplay();
    });

    this.visibilityHandler = () => {
      if (document.hidden) {
        this.stopAutoplay();
      } else {
        this.startAutoplay();
      }
    };

    this.resizeHandler = () => this.updateTrackHeight();
    document.addEventListener('visibilitychange', this.visibilityHandler);
    window.addEventListener('resize', this.resizeHandler);

    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(() => this.updateTrackHeight());
      this.slides.forEach((slide) => this.resizeObserver.observe(slide));
    }

    this.updateState(0);
    this.startAutoplay();
  }

  disconnectedCallback() {
    this.stopAutoplay();
    document.removeEventListener('visibilitychange', this.visibilityHandler);
    window.removeEventListener('resize', this.resizeHandler);
    this.resizeObserver?.disconnect();
  }

  handleScroll() {
    if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);

    this.scrollFrame = requestAnimationFrame(() => {
      const slideWidth = this.track.clientWidth;
      if (!slideWidth) return;

      const nextIndex = Math.max(
        0,
        Math.min(this.slides.length - 1, Math.round(this.track.scrollLeft / slideWidth))
      );

      if (nextIndex !== this.currentIndex) this.updateState(nextIndex);
    });
  }

  goTo(index) {
    const safeIndex = Math.max(0, Math.min(this.slides.length - 1, index));
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.track.scrollTo({
      left: this.track.clientWidth * safeIndex,
      behavior: reducedMotion ? 'auto' : 'smooth',
    });

    this.updateState(safeIndex);
  }

  updateState(index) {
    this.currentIndex = index;

    this.slides.forEach((slide, slideIndex) => {
      slide.setAttribute('aria-hidden', slideIndex === index ? 'false' : 'true');
    });

    this.dots.forEach((dot, dotIndex) => {
      const isActive = dotIndex === index;
      dot.classList.toggle('is-active', isActive);
      dot.setAttribute('aria-current', isActive ? 'true' : 'false');
    });

    this.updateTrackHeight();
  }

  updateTrackHeight() {
    const activeSlide = this.slides[this.currentIndex];
    if (!activeSlide || !this.track) return;

    requestAnimationFrame(() => {
      this.track.style.height = `${activeSlide.scrollHeight}px`;
    });
  }

  startAutoplay() {
    if (
      this.dataset.autoplay !== 'true' ||
      this.slides.length < 2 ||
      document.hidden ||
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    ) {
      return;
    }

    this.stopAutoplay();
    const interval = Number(this.dataset.interval) || 5000;

    this.autoplayTimer = window.setInterval(() => {
      this.goTo((this.currentIndex + 1) % this.slides.length);
    }, interval);
  }

  stopAutoplay() {
    if (!this.autoplayTimer) return;
    window.clearInterval(this.autoplayTimer);
    this.autoplayTimer = null;
  }

  restartAutoplay() {
    this.stopAutoplay();
    this.startAutoplay();
  }
}

if (!customElements.get('product-testimonials')) {
  customElements.define('product-testimonials', ProductTestimonials);
}
